import { MissingInfoKeys } from 'src/ts/models/user-info'
import { AuthStorageKeys } from '../../../config/auth-enumerations'
import { sanitizeUrl } from '@gbds/core/src/ts/utils/helper-utils'
import { parseTokenForEntitlements } from '../../../utils/auth/verify-token'
import { BRANDS_DETOUR_URL } from '../../../config/paths'

export class MissingInfoApi {
	processMissingInfo: () => Promise<void>
	completed: (status: any) => void

	constructor() {
		this.processMissingInfo = () => this.processMissingInfoAndRedirect()
		this.completed = () => this.completeProcess()
		document.addEventListener('gb-myaccountReset', e => checkMissingInfo(e as CustomEvent))
	}

	_cache: MissingInfoKeys[]
	_currentProcess: MissingInfoKeys
	MISSING_INFO_STORAGE_KEY = 'GBPE_API_CACHE_MISSINGINFO'

	private getSessionStorage(storageKey: string) {
		try {
			if (sessionStorage.getItem('GBPE_API_CACHE_MISSINGINFO') === 'COMPLETED') {
				return
			}
			this._cache =
				JSON.parse(sessionStorage.getItem(storageKey))['actionRequired'] ||
				JSON.parse(sessionStorage.getItem(storageKey))
			if (this._cache.length === 0) {
				sessionStorage.setItem(this.MISSING_INFO_STORAGE_KEY, 'COMPLETED')
				return
			}
			// sorting based on priorities
			this._cache.sort((a, b) => (!b.priority || a.priority < b.priority ? -1 : 1))
			sessionStorage.setItem(this.MISSING_INFO_STORAGE_KEY, JSON.stringify(this._cache))
			return this._cache
		} catch (e) {
			console.log('Exception in fetching from Missing Info session storage!!')
		}
	}

	get isProcessPending() {
		this._cache = this.getSessionStorage(this.MISSING_INFO_STORAGE_KEY)
		// 1. Empty missing info - no pending processes
		if (!this._cache) {
			return false
		}

		this._currentProcess = this._cache[0]
		// 2. Non-Empty URL returned from API - proceed with missing info redirection
		if (this._currentProcess?.url) {
			return true
		}
		// 3. Empty URL returned from API - no pending processes
		if (this._currentProcess.url === '' || this._currentProcess.url === undefined) {
			this._currentProcess.completed = 'true'
			return false
		}
		return false
	}

	/**
	 * @param redirectURL
	 * redirect page to the cached URL or override URL
	 *
	 **/
	public async processMissingInfoAndRedirect() {
		if (this.isProcessPending) {
			await window.gbpe.auth.loaded

			//Make sure this is not called on detour page regarding region mismatch
			if (window.gbpe.auth.currentAuthState === 1 && location?.href?.includes(BRANDS_DETOUR_URL) === false) {
				this.redirectToTCPS(this._currentProcess.url)
			}
		} else {
			console.log('No pending MissingInfo!!')
		}
	}

	// after redirecting to the given URL, mark that process as IN_PROGRESS
	private redirectToTCPS(url: string) {
		this._currentProcess.completed = 'IN_PROGRESS'
		this._cache[0] = this._currentProcess
		sessionStorage.setItem(this.MISSING_INFO_STORAGE_KEY, JSON.stringify(this._cache))
		window.location.href = sanitizeUrl(url)
	}

	/**
	 * 1. Called from B2C apps to mark the current process as completed
	 * 2. Redirect to the page stored in state param
	 * */
	public completeProcess() {
		console.log('Missing info Process completed() called..')
		if (sessionStorage.getItem('GBPE_API_CACHE_MISSINGINFO') === 'COMPLETED') {
			console.log('Missing Info Process is COMPLETED.No pending process!!')
		} else {
			try {
				this._cache = JSON.parse(sessionStorage.getItem('GBPE_API_CACHE_MISSINGINFO'))
				this._cache = this._cache.filter(a => a.completed !== 'IN_PROGRESS')
				sessionStorage.setItem(this.MISSING_INFO_STORAGE_KEY, JSON.stringify(this._cache))
				console.log('process completed..')
				const stateParam = sessionStorage.getItem(AuthStorageKeys.state)
				window.location.href = stateParam ? sanitizeUrl(stateParam) : window.location.origin
			} catch (e) {
				console.log('Cannot complete processs!!')
			}
		}
	}
}

export const checkMissingInfo = async (event?) => {
	// When reset is called on Missing Info object
	// Event listener only for missing info else return
	if (event?.type === 'gb-myaccountReset' && event?.detail?.itemName !== 'missingInfo') {
		return
	}
	await window.gbpe.auth.loaded

	//Temporary solution to check for entitled User based on token and skip missinginfo if the user is entitled
	const entitlement =
		window.gbpe.auth.currentAuthState === 1 ? parseTokenForEntitlements(window.gbpe.auth.idToken) : ''
	const CELESTIQ_ENTITLEMENT = ['CELESTIQVIP', 'JDCONCIERGE']
	const isCelestiq = entitlement ? CELESTIQ_ENTITLEMENT.filter(item => entitlement.includes(item)).length > 0 : false
	const entitledUser = isCelestiq === true

	if (entitledUser === true) {
		window.console.warn('Missing Info calls are blocked for Entitled users')
	}

	try {
		if (sessionStorage.getItem('GBPE_API_CACHE_MISSINGINFO') === 'COMPLETED') {
			console.log('Missing Info Api Process Completed!!')
		} else {
			const cache = JSON.parse(sessionStorage.getItem('GBPE_API_CACHE_MISSINGINFO'))
			const isProcessPending = cache === 'COMPLETED' ? false : true // misssing info check completed
			const inProgress = cache?.filter(a => a.completed === 'IN_PROGRESS' && window.location.href.includes(a.url))
				.length
				? true
				: false // misssing info check im progress/current page
			const authState = window.gbpe.auth.currentAuthState
			const recheckMissingInfo = cache?.length === 0 ? true : false
			if (recheckMissingInfo) {
				const missingInfo = await window.gbpe.user.missingInfo
				missingInfo.reset()
				missingInfo.processMissingInfo()
			}
			if (
				authState === 1 &&
				window.gbpe.auth.isMissingInfoRequired &&
				isProcessPending &&
				!inProgress &&
				entitledUser === false
			) {
				const missingInfo = await window.gbpe.user.missingInfo
				missingInfo.processMissingInfo()
			}
		}
	} catch (e) {
		console.log('Missing Info Api call failed!!')
	}
}
